/**
 * DatiPraticaCard — F-006 Phase 5 (IMPL-P5-005).
 *
 * Read-only card showing the pratica's anagrafica + immobile + contratto data.
 * Uses semantic `<dl>/<dt>/<dd>` so screen readers expose the key/value
 * relationships.
 *
 * The pratica detail in V1 has flat fields (proprietarioNome/CF, indirizzo,
 * canone, etc.) — sections are visual groupings only.
 */
import type { PraticaDetailView } from '@app/shared';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDate } from '../lib/format';

interface DatiPraticaCardProps {
  pratica: PraticaDetailView;
}

function formatCurrency(amount?: number): string {
  if (amount === undefined) return '—';
  try {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
  } catch {
    return `€ ${amount.toFixed(2)}`;
  }
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="text-sm">{value ?? '—'}</dd>
    </div>
  );
}

export function DatiPraticaCard({ pratica }: DatiPraticaCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Dati pratica</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
          <Field label="Tipologia" value={pratica.tipologia} />
          <Field label="Stato" value={pratica.stato} />

          {/* Sezione Locatore */}
          <Field label="Proprietario" value={pratica.proprietarioNome} />
          <Field label="CF proprietario" value={pratica.proprietarioCF ?? '—'} />

          {/* Sezione Conduttore */}
          <Field label="Inquilino" value={pratica.inquilinoNome} />
          <Field label="CF inquilino" value={pratica.inquilinoCF ?? '—'} />

          {/* Sezione Immobile */}
          <Field label="Indirizzo immobile" value={pratica.indirizzoImmobile} />
          <Field label="Città" value={pratica.cittaImmobile ?? '—'} />

          {/* Sezione Contratto */}
          <Field label="Canone mensile" value={formatCurrency(pratica.canoneMensile)} />
          <Field
            label="Data stipula"
            value={
              pratica.dataStipula ? (
                <time dateTime={pratica.dataStipula}>{formatDate(pratica.dataStipula)}</time>
              ) : (
                '—'
              )
            }
          />

          <Field label="Protocollo AdE" value={pratica.protocolloAde ?? '—'} />
          <Field
            label="Creata il"
            value={
              <time dateTime={pratica.createdAt}>{formatDate(pratica.createdAt)}</time>
            }
          />
        </dl>
      </CardContent>
    </Card>
  );
}
